import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';

/** Visual appearance — maps to a Material button directive internally. */
export type AppButtonVariant = 'filled' | 'outlined' | 'text' | 'elevated';

/**
 * Semantic color. `primary` is the Nabsys brand treatment pinned in CSS
 * (filled = teal→green gradient, outlined/text = #318f9a) so it matches the
 * app's current look exactly. `accent`/`warn` pass through to the Material
 * theme's palette.
 */
export type AppButtonColor = 'primary' | 'accent' | 'warn';

/**
 * Shared button — the preferred button component. Replaces the deprecated
 * {@link ButtonsComponent} (`<app-buttons>`).
 *
 * Consumers reason about ONE thing — `variant` (what it is) — and optionally a
 * semantic `color`. Appearance maps to the correct Material directive here, so
 * there are no stringly-typed style classes to pair by hand.
 *
 *   <app-button (click)="save()">Save</app-button>                  <!-- filled / primary -->
 *   <app-button variant="outlined">Cancel</app-button>
 *   <app-button [loading]="saving()">Save</app-button>              <!-- spinner + auto-disable -->
 *   <app-button class="flow-cta" [fullWidth]="true">Sign in</app-button>  <!-- flat teal, full width -->
 *   <app-button [routerLink]="['/analysis']" variant="text">Back</app-button>  <!-- renders <a> -->
 *   <app-button href="https://…" target="_blank">Docs</app-button>            <!-- external link -->
 *
 * Why this exists / how it differs from `<app-buttons>`:
 * - ONE typed `variant` axis (filled/outlined/text/elevated) instead of two
 *   stringly-typed axes (`buttonType` + `buttonStyle`) that had to be paired by
 *   hand with no compiler help and no enforced valid combinations.
 * - Label via content projection (`<app-button>Save</app-button>`) instead of a
 *   `[name]` string input.
 * - Native `(click)` instead of a wrapped `output<object>` re-emit.
 * - Built-in `loading` (spinner + auto-disable), `fullWidth`, and disabled
 *   styling — no `-disabled` style class to keep in sync by hand.
 * - `OnPush` change detection.
 *
 * Brand look: the primary treatment (gradient / #318f9a) is pinned in this
 * component's CSS. Override the fill per-instance via the `--app-btn-gradient`
 * custom property — see the shared `app-button.flow-cta` rule in styles.css for
 * the flat-teal variant used across the login flow.
 *
 * Anchor mode: setting `routerLink` or `href` renders an `<a>` (styled as a
 * button) instead of a `<button>` — covers the old `mat-router-link-button` /
 * `mat-link-button` types.
 *
 * Not covered here: icon-only buttons (use a raw `<button mat-icon-button>` with
 * an `aria-label`), menu items (use a raw `<button mat-menu-item>`), and
 * stepper-nav buttons (`matStepperNext` only attaches to a native `<button>`;
 * use a raw button or `(click)="stepper.next()"`).
 */
@Component({
  selector: 'app-button',
  standalone: true,
  imports: [NgTemplateOutlet, MatButtonModule, MatProgressSpinnerModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './button.component.html',
  styleUrl: './button.component.css',
  host: {
    '[class.app-button--full-width]': 'fullWidth()',
  },
})
export class ButtonComponent {
  readonly variant = input<AppButtonVariant>('filled');
  readonly color = input<AppButtonColor>('primary');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  readonly fullWidth = input(false, { transform: booleanAttribute });
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly ariaLabel = input<string>();

  /**
   * Anchor mode: set `routerLink` (in-app nav) or `href` (external) to render an
   * `<a>` styled as a button instead of a `<button>`. This is what replaces the
   * old `<app-buttons>` `mat-router-link-button` / `mat-link-button` types.
   */
  readonly routerLink = input<string | unknown[]>();
  readonly href = input<string>();
  readonly target = input<'_blank' | '_self' | '_parent' | '_top'>();

  /** A button in flight is non-interactive without the caller wiring it up. */
  readonly isDisabled = computed(() => this.disabled() || this.loading());

  /** Render an `<a>` when either link input is set. */
  readonly isLink = computed(() => this.routerLink() != null || this.href() != null);
  /** routerLink takes precedence over href when both are somehow provided. */
  readonly isRouterLink = computed(() => this.routerLink() != null);

  /**
   * `primary` renders colorless (like today's markup) so our pinned gradient/
   * accent CSS wins and the text color is identical to the current app.
   * Other colors defer to the Material theme's palette.
   */
  readonly matColor = computed<'accent' | 'warn' | null>(() => {
    const c = this.color();
    return c === 'primary' ? null : c;
  });

  /** CSS hook: primary is the pinned brand treatment (gradient / #318f9a). */
  readonly isPrimary = computed(() => this.color() === 'primary');
}
